import { format } from 'date-fns'
import {
  ConciliationType,
  PrefillOutcome,
  PREFILL_OUTCOME_CHIP_LABELS,
  PREFILLED_ROW_CHIP_LABEL,
  SourceType,
  ValidationStatus
} from '@/constants/ds-attendance.constants'
import type {
  DSAttendancePrefillEntry,
  DSAttendancePrefilledValues
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import type { DSAttendanceConciliateRowPayload } from '@/Network/Types/Requests/DsAttendanceRequest'

/**
 * ESP-02/29-04 — regras puras do "Espelho da Escala + Conferência" (mockup 05, aprovado em
 * 03/09). Tudo o que a grade decide sobre uma linha vive aqui: nenhuma decisão de estado,
 * contagem ou payload nasce no template do componente.
 *
 * NOMENCLATURA (decisão do usuário, 03/09): o gesto humano é "Conferência da folha" /
 * "Confirmar conferência" — NUNCA "atestado", que no domínio é atestado médico.
 */

/** Estado visível da linha. `PREFILLED` é a ausência de veredito, não um `PrefillOutcome`. */
export type MirrorRowState = 'PREFILLED' | PrefillOutcome

export interface MirrorRow {
  /** `null` só em linha extra recém-adicionada, que ainda não existe no backend. */
  entryId: number | null
  doctorStationId: number | null
  doctorName: string
  crm: string
  /** `HH:mm` — o que a célula editável mostra. */
  checkin: string
  checkout: string
  /** Valor original do prefill, exibido riscado quando o operador alterou a célula. */
  prefilledCheckin: string | null
  prefilledCheckout: string | null
  sourceType: SourceType | null
  prefillOutcome: PrefillOutcome | null
  validationStatus: string
  /**
   * BUG UI-8 (relatório 03/09) — `AUTOMATIC` quando o backend já casou a linha sozinho
   * (SINGLE_MATCH auto-confirmado); `MANUAL` quando o auditor escolheu o candidato;
   * `null` sem conciliação ainda. É o que autoriza "Desfazer conciliação" no menu ⋯.
   */
  conciliationType: ConciliationType | string | null
  /** `true` quando a linha nasceu do espelho: identidade NÃO editável (decisão 4). */
  isPrefillBorn: boolean
  /**
   * Conferência de assinatura e carimbo de ENTRADA / SAÍDA na folha física (pacote
   * 2026-09-10). Nasce MARCADA e o operador desmarca o que faltar na folha (decisão do
   * usuário, 10/09) — só um `false` explícito gravado no backend chega desmarcado. Mesma
   * regra de gate da grade clássica: sem as duas marcas a linha não avança para a auditoria.
   */
  hasDoctorSignature: boolean
  hasDoctorStamp: boolean
  /** O que o backend já tinha gravado, para saber se há marcação ainda não enviada. */
  savedDoctorSignature: boolean
  savedDoctorStamp: boolean
  /**
   * Cara-crachá V1 — página da folha em que esta linha está. Marcada à mão pelo operador
   * ("Marcar página atual") ou trazida pelo prefill; `null` enquanto ninguém marcou. Sem
   * OCR e sem detecção automática (D15).
   */
  pageNumber: number | null
  /** Página já gravada no backend, para distinguir marcação ainda não enviada. */
  savedPageNumber: number | null
}

/** `2026-09-21T22:00:00.000Z` → `19:00` no fuso do navegador. Vazio quando não há valor. */
export function formatTimeCell(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, 'HH:mm')
}

/**
 * `19:00` + `2026-09-21` → ISO. A saída anterior à entrada é do dia seguinte (plantão
 * noturno): mesma correção que o fluxo clássico de conciliação já aplicava.
 */
export function toISODateTime(time: string, referenceDate: string): string {
  return new Date(`${referenceDate}T${time}:00`).toISOString()
}

function isPrefillBornEntry(entry: DSAttendancePrefillEntry): boolean {
  return entry.sourceType === SourceType.PREFILLED || Boolean(entry.prefilledValues)
}

/** Extraído à parte para não empurrar `buildMirrorRows` acima do limite de complexidade. */
function resolveEntryConciliationType(
  entry: DSAttendancePrefillEntry
): ConciliationType | string | null {
  return entry.conciliationType ?? null
}

function prefilledTime(
  prefilled: DSAttendancePrefilledValues | null | undefined,
  field: 'checkinReal' | 'checkoutReal'
): string | null {
  const raw = prefilled?.[field]
  if (!raw) return null
  const formatted = formatTimeCell(raw)
  return formatted || null
}

/**
 * Mesmo fallback do `resolveEntryStationId` do backend: `revert-validation` e
 * `resubmit` zeram a coluna `doctor_station_id`, mas o snapshot do espelho
 * sobrevive — sem isto o id do plantão desaparecia da grade justamente depois
 * de recomeçar a auditoria.
 */
function resolveRowStationId(entry: DSAttendancePrefillEntry): number | null {
  return entry.doctorStationId ?? entry.prefilledValues?.doctorStationId ?? null
}

/** Monta as linhas da grade a partir da resposta do prefill, na ordem devolvida. */
export function buildMirrorRows(entries: DSAttendancePrefillEntry[]): MirrorRow[] {
  return entries.map((entry) => {
    const prefilled = entry.prefilledValues ?? null
    // Uma avaliação só: `pageNumber` alimenta o valor em tela E o "já gravado".
    const pageNumber = entry.pageNumber ?? null
    return {
      entryId: entry.entryId,
      doctorStationId: resolveRowStationId(entry),
      doctorName: entry.rawDoctorName ?? '',
      crm: entry.rawCrm ?? '',
      checkin: formatTimeCell(entry.checkinReal),
      checkout: formatTimeCell(entry.checkoutReal),
      prefilledCheckin:
        prefilledTime(prefilled, 'checkinReal') ?? formatTimeCell(entry.checkinReal),
      prefilledCheckout:
        prefilledTime(prefilled, 'checkoutReal') ?? formatTimeCell(entry.checkoutReal),
      sourceType: entry.sourceType ?? null,
      prefillOutcome: entry.prefillOutcome ?? null,
      validationStatus: entry.validationStatus ?? ValidationStatus.PENDING,
      conciliationType: resolveEntryConciliationType(entry),
      isPrefillBorn: isPrefillBornEntry(entry),
      // Pré-marcadas: null/undefined/true → marcada; só `false` explícito → desmarcada.
      // `saved*` recebe o MESMO valor inicial para a tela não acusar trabalho não enviado
      // logo ao abrir o espelho.
      hasDoctorSignature: entry.hasDoctorSignature !== false,
      hasDoctorStamp: entry.hasDoctorStamp !== false,
      savedDoctorSignature: entry.hasDoctorSignature !== false,
      savedDoctorStamp: entry.hasDoctorStamp !== false,
      pageNumber,
      savedPageNumber: pageNumber
    }
  })
}

/** Linha vazia para o "+ Adicionar linha" — extra, sem entry e com identidade editável. */
export function createExtraRow(): MirrorRow {
  return {
    entryId: null,
    doctorStationId: null,
    doctorName: '',
    crm: '',
    checkin: '',
    checkout: '',
    prefilledCheckin: null,
    prefilledCheckout: null,
    sourceType: SourceType.MANUAL,
    prefillOutcome: PrefillOutcome.EXTRA,
    validationStatus: ValidationStatus.PENDING,
    conciliationType: null,
    isPrefillBorn: false,
    hasDoctorSignature: true,
    hasDoctorStamp: true,
    savedDoctorSignature: true,
    savedDoctorStamp: true,
    pageNumber: null,
    savedPageNumber: null
  }
}

/**
 * Linha "em uso" para efeito de conferência: tem identidade e não foi marcada como
 * ausente. Linha extra ainda em branco e linha "não consta na folha" ficam de fora —
 * exatamente as que `toConciliateRows` também não envia.
 */
export function isRowInUse(row: MirrorRow): boolean {
  if (resolveRowState(row) === PrefillOutcome.ABSENT) return false
  return row.doctorName.trim() !== '' || row.crm.trim() !== ''
}

/**
 * Linhas em uso com assinatura/carimbo de entrada ou de saída desmarcados pelo operador.
 * É o gate de "Avançar para auditoria" no espelho (pacote 2026-09-10) — mesma regra que
 * a grade clássica aplica via `classifyRow` ('partial' quando um checkbox está desmarcado).
 * Como as caixas nascem marcadas, o gate só aparece depois de o operador desmarcar algo.
 */
export function rowsMissingSignatureStamp(rows: MirrorRow[]): number[] {
  return rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => isRowInUse(row) && !(row.hasDoctorSignature && row.hasDoctorStamp))
    .map(({ index }) => index)
}

/** `true` quando a marcação da linha difere do que o backend já gravou (RN-10). */
export function hasUnsavedSignatureStamp(row: MirrorRow): boolean {
  return (
    row.hasDoctorSignature !== row.savedDoctorSignature ||
    row.hasDoctorStamp !== row.savedDoctorStamp
  )
}

/**
 * Cara-crachá V1 — a página marcada nesta sessão ainda não foi para o backend. Conta como
 * trabalho não enviado no guarda de saída, igual à assinatura/carimbo.
 */
export function hasUnsavedPageNumber(row: MirrorRow): boolean {
  return row.pageNumber !== row.savedPageNumber
}

/**
 * BUG UI-8 — mesmo critério de `isPreConfirmedConciliationRow` (fluxo clássico,
 * `DsAttendanceAwaitingAudit.vue`), aplicado a `MirrorRow`: só uma conciliação SINGLE_MATCH
 * resolvida sozinha pelo backend é reversível por "Desfazer conciliação" — uma escolhida
 * manualmente pelo auditor (`MANUAL`) não oferece essa ação.
 */
export function isAutomaticallyConciliated(row: MirrorRow): boolean {
  return (
    row.validationStatus === ValidationStatus.CONCILIATED &&
    row.conciliationType === ConciliationType.AUTOMATIC
  )
}

/** `true` quando algum horário da linha difere do que o prefill escreveu. */
export function isRowEdited(row: MirrorRow): boolean {
  if (!row.isPrefillBorn) return false
  return (
    (row.prefilledCheckin !== null && row.checkin !== row.prefilledCheckin) ||
    (row.prefilledCheckout !== null && row.checkout !== row.prefilledCheckout)
  )
}

/**
 * Estado da linha, na precedência do mockup: o veredito já gravado no backend vence; sem
 * veredito, a edição local vence; sem edição, a linha do espelho é "Pré-preenchido" e a
 * digitada à mão é "Extra".
 */
export function resolveRowState(row: MirrorRow): MirrorRowState {
  if (row.prefillOutcome === PrefillOutcome.ABSENT) return PrefillOutcome.ABSENT
  if (row.prefillOutcome === PrefillOutcome.EXTRA) return PrefillOutcome.EXTRA
  if (isRowEdited(row)) return PrefillOutcome.EDITED
  if (row.prefillOutcome) return row.prefillOutcome
  return row.isPrefillBorn ? 'PREFILLED' : PrefillOutcome.EXTRA
}

/** Rótulo do chip de "Estado da linha". */
export function rowStateChipLabel(state: MirrorRowState): string {
  return state === 'PREFILLED' ? PREFILLED_ROW_CHIP_LABEL : PREFILL_OUTCOME_CHIP_LABELS[state]
}

/**
 * As linhas que o OK global fecha: nascidas do espelho, intocadas e ainda sem veredito. É o
 * número do botão "✔ Confere com a escala (N)" — nunca inclui editada, ausente ou extra.
 */
export function pendingConferenceRows(rows: MirrorRow[]): MirrorRow[] {
  return rows.filter((row) => resolveRowState(row) === 'PREFILLED')
}

export type PrefillOutcomeTally = Record<PrefillOutcome, number>

/**
 * Contagem por desfecho como o modal de conferência mostra: as linhas pendentes já contam
 * como `CONFIRMED`, porque é exatamente isso que confirmar vai gravar.
 */
export function tallyOutcomes(rows: MirrorRow[]): PrefillOutcomeTally {
  const tally: PrefillOutcomeTally = {
    [PrefillOutcome.CONFIRMED]: 0,
    [PrefillOutcome.EDITED]: 0,
    [PrefillOutcome.ABSENT]: 0,
    [PrefillOutcome.EXTRA]: 0
  }

  for (const row of rows) {
    const state = resolveRowState(row)
    if (state === 'PREFILLED') tally[PrefillOutcome.CONFIRMED] += 1
    else tally[state] += 1
  }

  return tally
}

export interface PrefillCoverage {
  total: number
  contemplated: number
  percentage: number
}

/**
 * Assertividade do espelho: quantas das linhas nascidas do prefill seguem valendo o que a
 * escala previu. Alimenta o indicador discreto "Previsão contemplou X de Y linhas".
 */
export function prefillCoverage(rows: MirrorRow[]): PrefillCoverage {
  const born = rows.filter((row) => row.isPrefillBorn)
  const contemplated = born.filter((row) => {
    const state = resolveRowState(row)
    return state === 'PREFILLED' || state === PrefillOutcome.CONFIRMED
  }).length

  return {
    total: born.length,
    contemplated,
    percentage: born.length === 0 ? 0 : Math.round((contemplated / born.length) * 100)
  }
}

export function prefillCoverageLabel(coverage: PrefillCoverage): string {
  const linhas = coverage.total === 1 ? 'linha' : 'linhas'
  return `Previsão contemplou ${coverage.contemplated} de ${coverage.total} ${linhas} (${coverage.percentage}%)`
}

/**
 * Payload de `/conciliate` a partir da grade do espelho. O `entryId` é ecoado SEMPRE que a
 * linha já existe no container — é o que fecha o BUG-01 (merge posicional). Linhas marcadas
 * como ausentes não voltam para a conciliação: elas já são DIVERGENT no backend.
 */
export function toConciliateRows(
  rows: MirrorRow[],
  referenceDate: string
): DSAttendanceConciliateRowPayload[] {
  return rows.filter(isRowInUse).map((row) => {
    const payload: DSAttendanceConciliateRowPayload = {
      rawDoctorName: row.doctorName,
      rawCrm: row.crm,
      // O que o operador conferiu na folha física — nunca mais `true` fixo: o backend
      // rejeita a conciliação com caixa desmarcada (gate do PR 2748).
      hasDoctorSignature: row.hasDoctorSignature,
      hasDoctorStamp: row.hasDoctorStamp
    }

    if (row.entryId !== null) payload.entryId = row.entryId

    // Cara-crachá V1 — a página marcada viaja no mesmo `/conciliate` que já grava a linha:
    // o contrato de `rows` aceita `pageNumber`, não há endpoint novo a criar.
    if (row.pageNumber !== null) payload.pageNumber = row.pageNumber

    if (row.checkin) payload.checkinReal = toISODateTime(row.checkin, referenceDate)
    if (row.checkout) payload.checkoutReal = toISODateTime(row.checkout, referenceDate)

    if (payload.checkinReal && payload.checkoutReal) {
      const checkin = new Date(payload.checkinReal)
      const checkout = new Date(payload.checkoutReal)
      if (checkout < checkin) {
        checkout.setDate(checkout.getDate() + 1)
        payload.checkoutReal = checkout.toISOString()
      }
    }

    return payload
  })
}

export interface MirrorCandidate {
  doctorStationId: number
  name: string
  crm: string
  /** "Sugerido" — já escalado neste turno/unidade e ainda sem linha resolvida na folha. */
  isSuggested: boolean
}

/**
 * Candidatos para "Substituir médico" (decisão 4): SÓ gente da base, nunca texto livre. A
 * fonte é o próprio espelho — cada linha do prefill carrega uma `DoctorStation` real do dia,
 * o que é literalmente "quem estava escalado neste turno/unidade".
 *
 * INVARIANTE DURO: escolher um candidato muda apenas a entry da folha. A `DoctorStation` /
 * escala oficial JAMAIS é alterada por este fluxo.
 */
export function buildReplacementCandidates(
  rows: MirrorRow[],
  currentRow: MirrorRow,
  search: string
): MirrorCandidate[] {
  const term = search.trim().toLowerCase()

  return rows
    .filter((row) => row.doctorStationId !== null)
    .filter((row) => row.doctorStationId !== currentRow.doctorStationId)
    .filter(
      (row) =>
        term === '' ||
        row.doctorName.toLowerCase().includes(term) ||
        row.crm.toLowerCase().includes(term)
    )
    .map((row) => ({
      doctorStationId: row.doctorStationId as number,
      name: row.doctorName,
      crm: row.crm,
      isSuggested: resolveRowState(row) === 'PREFILLED'
    }))
    .sort((a, b) => Number(b.isSuggested) - Number(a.isSuggested))
}
